import { useState, useCallback } from 'react'
import { Building2, Search, Trash2 } from 'lucide-react'
import { FbModal as Modal } from 'components/UI/Modal'
import { CompanySearchModal } from 'components/Samples/CompanySearchModal'
import { cn } from 'components/UI/cn'
import type { ContactCompanySearchResult } from 'models/companies'

interface RelatedCompany {
  id: number
  name: string
  sub_category: {
    id: number
    name: string
    slug: string
    description: string
  } | null
  url?: string
}

interface EditRelatedCompaniesModalProps {
  show: boolean
  onClose: () => void
  dealId: number
  primaryCompanyId: number
  currentRelatedCompanies: RelatedCompany[]
  onSave: (added: number[], removed: number[]) => Promise<void>
}

const VISIBLE_TABS = ['firstbite', 'unmatched'] as const

export function EditRelatedCompaniesModal({
  show,
  onClose,
  primaryCompanyId,
  currentRelatedCompanies,
  onSave,
}: EditRelatedCompaniesModalProps) {
  // Local state for the working copy of related companies.
  // Component unmounts when !show, so state resets from props on each open.
  const [localCompanies, setLocalCompanies] = useState<RelatedCompany[]>(() => [
    ...currentRelatedCompanies,
  ])
  const [selectedForRemoval, setSelectedForRemoval] = useState<Set<number>>(
    new Set()
  )
  const [showCompanySearch, setShowCompanySearch] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const handleAddCompany = useCallback(
    (result: ContactCompanySearchResult) => {
      const ccId = result.contact_company_id
      if (!ccId) return
      // Don't add the primary company or duplicates
      if (ccId === primaryCompanyId) return
      if (localCompanies.some((c) => c.id === ccId)) return

      const newCompany: RelatedCompany = {
        id: ccId,
        name: result.name,
        sub_category: result.sub_category_name
          ? {
              id: result.sub_category ?? 0,
              name: result.sub_category_name,
              slug: '',
              description: '',
            }
          : null,
      }
      setLocalCompanies((prev) => [...prev, newCompany])
      setShowCompanySearch(false)
    },
    [primaryCompanyId, localCompanies]
  )

  const handleToggleRemoval = useCallback((companyId: number) => {
    setSelectedForRemoval((prev) => {
      const next = new Set(prev)
      if (next.has(companyId)) {
        next.delete(companyId)
      } else {
        next.add(companyId)
      }
      return next
    })
  }, [])

  const allSelected =
    localCompanies.length > 0 &&
    selectedForRemoval.size === localCompanies.length

  const handleToggleAll = useCallback(() => {
    if (allSelected) {
      setSelectedForRemoval(new Set())
    } else {
      setSelectedForRemoval(new Set(localCompanies.map((c) => c.id)))
    }
  }, [allSelected, localCompanies])

  const handleSave = useCallback(async () => {
    const originalIds = new Set(currentRelatedCompanies.map((c) => c.id))

    // Companies added in this session (not in original, not marked for removal)
    const added = localCompanies
      .filter((c) => !originalIds.has(c.id) && !selectedForRemoval.has(c.id))
      .map((c) => c.id)

    // Companies to remove: originally existed + marked for removal
    const removed = currentRelatedCompanies
      .filter((c) => selectedForRemoval.has(c.id))
      .map((c) => c.id)

    if (added.length === 0 && removed.length === 0) {
      onClose()
      return
    }

    setIsSaving(true)
    try {
      await onSave(added, removed)
      onClose()
    } finally {
      setIsSaving(false)
    }
  }, [
    currentRelatedCompanies,
    localCompanies,
    selectedForRemoval,
    onSave,
    onClose,
  ])

  if (!show) return null

  return (
    <>
      <Modal
        open={true}
        onOpenChange={(open) => !open && onClose()}
        title="Edit Related Companies"
        description="Check companies to remove, or search to add new ones"
        size="md"
        onAccept={handleSave}
        acceptButtonText="Save Changes"
        blockAccept={isSaving}
        onCancel={onClose}
        cancelButtonText="Cancel"
      >
        <div className="flex flex-col gap-4">
          {/* Search trigger */}
          <button
            type="button"
            onClick={() => setShowCompanySearch(true)}
            className="w-full flex items-center gap-2 pl-3 pr-4 py-2 h-10 text-sm text-fg-tertiary border border-line rounded-lg bg-surface-raised hover:bg-surface-hover text-left"
          >
            <Search size={13} className="text-fg-muted flex-shrink-0" />
            <span>Search companies to add...</span>
          </button>

          {/* Related companies list */}
          <div className="border-t border-line pt-2">
            <div className="flex items-center justify-between mb-2 min-h-9">
              <label className="flex items-center gap-2 cursor-pointer">
                {localCompanies.length > 0 && (
                  <input
                    type="checkbox"
                    ref={(el) => {
                      if (el) {
                        el.indeterminate =
                          selectedForRemoval.size > 0 && !allSelected
                      }
                    }}
                    checked={allSelected}
                    onChange={handleToggleAll}
                    className="h-4 w-4 rounded border-line text-fg-secondary accent-fg-secondary"
                  />
                )}
                <span className="text-xs font-medium text-fg-secondary uppercase tracking-wider">
                  Related Companies ({localCompanies.length})
                </span>
              </label>
              {selectedForRemoval.size > 0 && (
                <span
                  className="inline-flex justify-center items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-red-600 border-red-200 rounded-md bg-red-100"
                  style={{ borderWidth: 1 }}
                >
                  <Trash2 size={14} />
                  <span className="leading-[0.1rem]">
                    Remove {selectedForRemoval.size}{' '}
                    {selectedForRemoval.size === 1 ? 'company' : 'companies'}
                  </span>
                </span>
              )}
            </div>

            {localCompanies.length === 0 ? (
              <div className="py-6 text-center text-sm text-fg-secondary">
                No related companies yet. Search above to add one.
              </div>
            ) : (
              <div>
                {localCompanies.map((company) => {
                  const isSelected = selectedForRemoval.has(company.id)
                  return (
                    <label
                      key={company.id}
                      className={cn(
                        'flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-red-100 rounded-lg accent-red-500',
                        isSelected && 'bg-red-50'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleRemoval(company.id)}
                        className="h-4 w-4 rounded border-line text-red-600 focus:ring-red-500"
                      />
                      <Building2
                        size={16}
                        className="text-fg-tertiary flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <p
                          className={cn(
                            'text-sm font-medium truncate',
                            isSelected ? 'text-red-600 line-through' : 'text-fg'
                          )}
                        >
                          {company.name}
                        </p>
                        {company.sub_category && (
                          <p className="text-xs text-fg-secondary truncate">
                            {company.sub_category.name}
                          </p>
                        )}
                      </div>
                    </label>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Company search modal (opens on top) */}
      <CompanySearchModal
        open={showCompanySearch}
        onClose={() => setShowCompanySearch(false)}
        onSelectCompany={handleAddCompany}
        visibleTabs={[...VISIBLE_TABS]}
      />
    </>
  )
}
